import { useState } from "react";
import { motion, AnimatePresence, PanInfo } from "framer-motion";
import { Target, CheckCircle, Sparkles, ArrowRight, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";

interface OnboardingCarouselProps {
  onComplete: () => void;
  onSkip: () => void;
  onBack: () => void;
}

const slides = [
  {
    icon: Target,
    title: "Select personal health goals",
    description: "Choose what matters most to you: gut health, brain focus, energy, or more.",
    color: "from-[#52B788] to-[#40916C]",
  },
  {
    icon: ShoppingCart,
    title: "Generate evidence-based smart grocery lists",
    description: "We use USDA data and public health guidelines to build your personalized list.",
    color: "from-[#40916C] to-[#2D6A4F]",
  },
  {
    icon: CheckCircle,
    title: "Swipe to complete items while shopping",
    description: "Easy interaction to check off items as you shop. No fuss.",
    color: "from-[#2D6A4F] to-[#1B4332]",
  },
  {
    icon: Sparkles,
    title: "Your health goals, now shoppable",
    description: "Stop figuring out what you should buy. Start grocery shopping.",
    color: "from-[#1B4332] to-[#081C15]",
    showButton: true,
  },
];

export const OnboardingCarousel = ({ onComplete, onSkip, onBack }: OnboardingCarouselProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0);

  const handleDragEnd = (_: any, info: PanInfo) => {
    const threshold = 30;
    if (info.offset.x < -threshold && currentSlide < slides.length - 1) {
      setDirection(1);
      setCurrentSlide((prev) => prev + 1);
    } else if (info.offset.x > threshold && currentSlide > 0) {
      setDirection(-1);
      setCurrentSlide((prev) => prev - 1);
    }
  };

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0,
    }),
  };

  const slide = slides[currentSlide];
  const Icon = slide.icon;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col overflow-hidden"
      style={{ backgroundColor: "#D8F3DC" }}
    >
      {/* Static background blobs */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute top-20 -left-20 w-64 h-64 rounded-full"
          style={{ backgroundColor: "#B7E4C7", filter: "blur(60px)" }}
        />
        <div
          className="absolute top-1/2 -right-20 w-72 h-72 rounded-full"
          style={{ backgroundColor: "#B7E4C7", filter: "blur(60px)" }}
        />
        <div
          className="absolute bottom-32 left-1/4 w-56 h-56 rounded-full"
          style={{ backgroundColor: "#B7E4C7", filter: "blur(60px)" }}
        />
      </div>

      {/* Back button */}
      <button
        onClick={onBack}
        className="absolute top-6 left-6 z-20 text-sm font-medium transition-colors"
        style={{ color: "#2D6A4F" }}
      >
        ← Back
      </button>

      {/* Skip button */}
      <button
        onClick={onSkip}
        className="absolute top-6 right-6 z-20 text-sm font-medium transition-colors"
        style={{ color: "#2D6A4F" }}
      >
        Skip
      </button>

      {/* Full-screen swipe area */}
      <motion.div
        className="flex-1 flex flex-col justify-center items-center relative z-10 cursor-grab active:cursor-grabbing"
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.3}
        onDragEnd={handleDragEnd}
      >
        <div className="px-8 w-full">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentSlide}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ type: "spring", stiffness: 350, damping: 35 }}
              className="flex flex-col items-center text-center"
            >
              <motion.div
                initial={{ scale: 0.8, rotate: -10 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
                className={`w-24 h-24 rounded-3xl bg-gradient-to-br ${slide.color} flex items-center justify-center mb-8 shadow-lg overflow-hidden`}
              >
                {Icon && <Icon className="w-12 h-12 text-white" strokeWidth={1.5} />}
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-2xl font-bold mb-4 max-w-xs"
                style={{ color: "#081C15" }}
              >
                {slide.title}
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="max-w-xs leading-relaxed"
                style={{ color: "#1B4332" }}
              >
                {slide.description}
              </motion.p>

              {slide.showButton && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="mt-10"
                >
                  <Button
                    onClick={onComplete}
                    className="h-14 px-8 text-base font-semibold rounded-full transition-all shadow-lg hover:opacity-90"
                    style={{ 
                      backgroundColor: "#1B4332",
                      color: "#FFFFFF"
                    }}
                  >
                    Move Forward
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </motion.div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Fixed pagination dots at bottom */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-50 flex justify-center gap-2 py-6"
        style={{ backgroundColor: "#D8F3DC" }}
      >
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setDirection(index > currentSlide ? 1 : -1);
              setCurrentSlide(index);
            }}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === currentSlide
                ? "w-8"
                : "w-2 hover:opacity-70"
            }`}
            style={{ 
              backgroundColor: index === currentSlide ? "#2D6A4F" : "#95D5B2"
            }}
          />
        ))}
      </div>
    </div>
  );
};
